import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ error, info });
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const { error, info } = this.state;
    return (
      <div className="error-boundary">
        <div className="error-boundary-card">
          <div className="error-boundary-icon">💥</div>
          <h1>Something went wrong</h1>
          <p className="error-boundary-msg">{error.message}</p>
          <div className="error-boundary-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                // Bypass the SW cache to recover from a stale-bundle crash.
                if ('serviceWorker' in navigator) {
                  navigator.serviceWorker
                    .getRegistrations()
                    .then((rs) => Promise.all(rs.map((r) => r.unregister())))
                    .finally(() => window.location.reload());
                } else {
                  window.location.reload();
                }
              }}
            >
              Reload (clear cache)
            </button>
            <button className="btn" onClick={() => this.setState({ error: null, info: null })}>
              Dismiss
            </button>
          </div>
          <details className="error-boundary-details">
            <summary>Technical details</summary>
            <pre>{error.stack ?? String(error)}</pre>
            {info?.componentStack ? <pre>{info.componentStack}</pre> : null}
          </details>
        </div>
      </div>
    );
  }
}
